import OktaAuth from "@okta/okta-auth-js";
import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { IdToken } from "@auth0/auth0-spa-js";
import { getConfig } from "../configuration";
import { resolveCachedEntraAccount } from "../auth/entraAccount";
import getApiConfig from "./apiConfig";
import { gzipIfWorthwhile } from "./requestCompression";

type ApiRequestOptions = {
  templateId?: string | null;
};

type AuthWindow = Window & {
  authInstance?: IPublicClientApplication;
  oktaInstance?: OktaAuth;
};

/** Margin for clock skew and flight time. */
const ID_TOKEN_MIN_REMAINING_SECONDS = 120;

/**
 * `acquireTokenSilent` guarantees a fresh *access* token for the requested scopes but returns the
 * ID token from cache, so it reports success while handing back one that lapsed an hour ago. The
 * Portal authenticates on the ID token, so that 401s every request while the add-in still shows
 * the user as signed in. A missing `exp` counts as unusable rather than trusted.
 */
function idTokenNeedsRefresh(result: AuthenticationResult): boolean {
  const exp = (result.idTokenClaims as { exp?: number } | undefined)?.exp;
  if (!exp) {
    return true;
  }
  return exp - Math.floor(Date.now() / 1000) < ID_TOKEN_MIN_REMAINING_SECONDS;
}

async function getFreshIdToken(instance: IPublicClientApplication, account: AccountInfo): Promise<string> {
  const request = {
    scopes: ["openid", "profile", "email"],
    account,
  };

  try {
    const result = await instance.acquireTokenSilent(request);
    if (!idTokenNeedsRefresh(result)) {
      return result.idToken;
    }
    const refreshed = await instance.acquireTokenSilent({ ...request, forceRefresh: true });
    return refreshed.idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const result = await instance.loginPopup(request);
      return result.idToken;
    }
    throw error;
  }
}

const api = {
  templateId: "",
  connectionName: "",

  setTemplateId(templateId: string) {
    this.templateId = templateId;
  },

  setConnectionName(connectionName: string) {
    this.connectionName = connectionName;
  },

  headers(authToken: string, identityProvider: string, options: ApiRequestOptions = {}) {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: authToken ? `Bearer ${authToken}` : "",
      "X-Bmx-Api-Key": identityProvider ? "" : getConfig().apiKey,
      "X-Bmx-Connection-Name": this.connectionName,
    };

    const templateId = Object.prototype.hasOwnProperty.call(options, "templateId")
      ? options.templateId
      : this.templateId;
    if (templateId) {
      headers["X-Bmx-Template-Id"] = templateId;
    }

    return headers;
  },
  async fetch(url: string, authToken: string, identityProvider = "Entra", method = "GET", body: any = null, options: ApiRequestOptions = {}) {
    const authWindow = window as AuthWindow;

    if (identityProvider === "Entra") {
      const instance = authWindow.authInstance;
      if (!instance) {
        throw new Error("MSAL auth instance is not available.");
      }
      // A reload leaves the cache populated but no active account, so re-resolve rather than fail.
      const account = resolveCachedEntraAccount(instance);
      if (!account) {
        throw new Error("No active MSAL account is available.");
      }
      instance.setActiveAccount(account);
      authToken = await getFreshIdToken(instance, account);
    } else if (identityProvider === "Okta") {
      const oktaInstance = authWindow.oktaInstance;
      if (!oktaInstance) {
        throw new Error("Okta auth instance is not available.");
      }
      const idTokenObj = (await oktaInstance.tokenManager.get("idToken")) as any as IdToken;
      authToken = idTokenObj?.idToken;
    }

    if (url.startsWith("http")) {
      return await fetch(url, {
        method: method,
      });
    }

    const completeUrl = `${getApiConfig().baseUrl}/${url}`;
    const headers = this.headers(authToken, identityProvider, options);
    const serialisedBody = body != null ? JSON.stringify(body) : null;

    // Large bodies (the base64 workbook on template save) go up gzipped. Content-Type stays
    // application/json — it describes the payload, Content-Encoding describes the wrapping.
    const compressedBody = serialisedBody != null ? gzipIfWorthwhile(serialisedBody) : null;
    if (compressedBody != null) {
      headers["Content-Encoding"] = "gzip";
    }

    return await fetch(completeUrl, {
      method: method,
      headers: headers,
      body: compressedBody != null ? compressedBody : serialisedBody,
    });
  },
  async post(url: string, body: any, authToken: string, identityProvider = "Entra", options: ApiRequestOptions = {}) {
    return await this.fetch(url, authToken, identityProvider, "POST", body, options);
  },
  async delete(url: string, authToken: string, identityProvider = "Entra") {
    return await this.fetch(url, authToken, identityProvider, "DELETE");
  },
};

export default api;